import { useMemo, useState } from 'react';
import { DogCell, Pagination, DataTable, DateCell, StatTotal, StatHead, StatLabel, SplitRow, FilterControl, fmtNum, type Column, type FilterOption } from '../common';
import { useDeferredLoading } from '../../lib/useDeferredLoading';
import { formatOwnerLabel } from '../bookings/domain';
import { isoDate } from '../../lib/dates';
import { OwnerBookingModal } from '../owners/OwnerBookingModal';
import {
  useOwnersPage,
  DEFAULT_OWNER_SORT,
  type OwnerFilterKey,
  type OwnerSortKey,
  type OwnerSortState,
} from './useOwnersPage';
import type { BookingDog, Owner } from '../bookings/types';

// The one Dogs & Owners filter (reduced scope — status only, no Type/Size/Flags/
// Breed). 'all' is the deselected/empty state, so it isn't an option here.
// Icons mirror the Dogs & Owners stat strip (Regulars=check, Newcomers=plus).
const OWNER_FILTER_OPTIONS: FilterOption[] = [
  { key: 'regulars', label: 'Regulars', icon: 'check' },
  { key: 'newcomers', label: 'Newcomers', icon: 'plus' },
  { key: 'boarding', label: 'Boarding now', icon: 'paw' },
];

// Dogs & Owners — the customer list. Ported from _prototype/views-extra.jsx
// DogsOwnersView: stat strip, search + filter chips, owners table (row → edit
// owner). Search / filter / pagination are now SERVER-side (the page fetches
// only the rows it shows, owners-server-search-paginate AC1/AC5); the stat strip
// + chip counts come from GET /api/owners/stats over ALL owners (AC4). visits /
// spend / boarding still come from the per-owner aggregate in the list rows.

// Owner CREATE now comes from the global top-bar New menu (CreateModalProvider);
// this view only owns the EDIT flow — clicking an owner row opens the modal in
// ownerEdit mode and refreshes the list on change.
export function DogsOwnersView() {
  const today = isoDate(new Date());
  const [filter, setFilter] = useState<OwnerFilterKey>('all');
  const [editOwnerId, setEditOwnerId] = useState<string | null>(null);
  const [sort, setSort] = useState<OwnerSortState>(DEFAULT_OWNER_SORT);
  // Toggle direction on the active column; a new column starts descending
  // (most-recent-first for the date column). Mirrors the bookings table sort.
  const onSort = (key: OwnerSortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  // Per-page search is gone (AC3) — the global top-bar search covers owners/dogs.
  const pageFilter = useMemo(() => ({ q: null, filter }), [filter]);
  const { state, stats, total, page, setPage, reload } = useOwnersPage(pageFilter, sort);

  const rows = state.phase === 'ready' ? state.owners : [];

  // Anti-flicker loading: hold the previous rows during a quick sort/filter/page
  // re-fetch, showing the skeleton only once the wait crosses the delay (the
  // first load, with no prior rows, shows it immediately).
  const { displayRows, showSkeleton } = useDeferredLoading(rows, state.phase === 'loading');

  const counts = {
    all: stats?.ownersTotal ?? 0,
    regulars: stats?.regulars ?? 0,
    newcomers: stats?.newcomers ?? 0,
    boarding: stats?.boarding ?? 0,
  };

  // One column config drives the owners table header + cells (same primitive as
  // Bookings). Each cell returns its complete grid child with its existing
  // layout class; typography comes from the cell-type tokens.
  const ownerColumns: Column<Owner, OwnerSortKey>[] = [
    {
      key: 'dog',
      label: 'Dog(s)',
      sortable: true,
      cell: (o) => <DogCell dogs={o.dogs as unknown as BookingDog[]} ownerName={formatOwnerLabel(o.contacts)} />,
    },
    {
      key: 'breed',
      label: 'Breed',
      sortable: true,
      cell: (o) => <div className="bt-breed">{[...new Set(o.dogs.map((d) => d.breed).filter(Boolean))].join(' · ')}</div>,
    },
    {
      key: 'contact',
      label: 'Contact',
      sortable: true,
      cell: (o) => (
        <div className="ot-contact">
          <div className="mono">{o.contacts[0]?.phone}</div>
          <div className="sub">{o.contacts[0]?.email}</div>
        </div>
      ),
    },
    {
      key: 'address',
      label: 'Address',
      sortable: true,
      cell: (o) => <div className="ot-address">{o.address}</div>,
    },
    {
      key: 'visits',
      label: 'Visits',
      sortable: true,
      cell: (o) => <div className="bt-num mono">{o.visits ?? 0}</div>,
    },
    {
      key: 'spend',
      label: 'Spend',
      sortable: true,
      cell: (o) => <div className="bt-num mono total">€{o.totalSpend ?? 0}</div>,
    },
    {
      key: 'updated',
      label: 'Last updated',
      sortable: true,
      cell: (o) => <DateCell value={o.updatedAt} />,
    },
    {
      key: 'added',
      label: 'Date added',
      sortable: true,
      cell: (o) => <DateCell value={o.createdAt} />,
    },
  ];

  return (
    <div className="dogs-view">
      <div className="bw-stats four">
        <div className="bw-stat">
          <StatLabel icon="user">Owners</StatLabel>
          <div className="val mono">{fmtNum(stats?.ownersTotal)}</div>
        </div>
        <div className="bw-stat">
          <StatTotal
            icon="paw"
            label="Dogs"
            total={stats?.dogsTotal}
            parts={[
              { label: 'Big', value: stats?.dogsBig },
              { label: 'Little', value: stats?.dogsLittle },
            ]}
          />
        </div>
        <div className="bw-stat">
          <StatHead icon="plus" label="Newcomers" detail="1 visit" />
          <div className="val mono">{fmtNum(stats?.newcomers)}</div>
        </div>
        <div className="bw-stat">
          <StatHead icon="check" label="Regulars" detail="10+ visits" />
          <div className="val mono">{fmtNum(stats?.regulars)}</div>
        </div>
      </div>

      <SplitRow
        right={
          <FilterControl
            label="Show"
            icon="paw"
            mode="single"
            options={OWNER_FILTER_OPTIONS}
            selected={filter === 'all' ? [] : [filter]}
            onChange={(keys) => setFilter((keys[0] as OwnerFilterKey) ?? 'all')}
          />
        }
      />

      <DataTable
        className="bookings-table owners-table"
        columns={ownerColumns}
        rows={displayRows}
        rowKey={(o) => o.id}
        sort={sort}
        onSort={onSort}
        onRowClick={(o) => setEditOwnerId(o.id)}
        rowClassName={(o) => (o.boardingNow ? 'is-boarding' : '')}
        // Keep the header mounted during a sort/filter/page re-fetch; the body
        // shows a column-matched skeleton — same look as the bookings table.
        // Delayed so a quick re-fetch holds the prior rows instead of flashing.
        loading={showSkeleton}
        loadingLabel="Loading owners…"
        // Keyed off displayRows, NOT `state.phase === 'ready'` — the same trap
        // the bookings table had. Holding a settled EMPTY result through the
        // delay stops the skeleton flashing, but the phase is still 'loading'
        // while that re-fetch is in flight, so a phase check dropped "No
        // matches" and left a header-only blank region instead (Codex, PR #150).
        empty={
          state.phase === 'error' ? (
            <div className="empty-state pad-30">Could not load owners.</div>
          ) : !showSkeleton && displayRows.length === 0 ? (
            <div className="empty-state pad-30">No matches.</div>
          ) : null
        }
      />

      <Pagination page={page} total={total} onPage={setPage} />

      {editOwnerId && (
        <OwnerBookingModal
          ownerId={editOwnerId}
          ownerSeed={rows.find((o) => o.id === editOwnerId) ?? null}
          initialMode="ownerEdit"
          today={today}
          onClose={() => setEditOwnerId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}
